import {ConversationStageType} from '@frontapp/insights/conversation_stages';

import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {TicketStatusModel} from '../../../../../libs/shared/core/src/models/api/ticketStatusModel';
import {SkippedActionReasonsEnum, SkippedActionTypesEnum} from '../../../../../src/models/api/activityModel';
import {
  ActivitySourceContactModel,
  ActivitySourceMacroModel,
  ActivitySourceModel,
  ActivitySourceOtherModel,
  ActivitySourceRuleModel,
} from '../../../../../src/models/api/activitySourceModel';
import {ApplicationModel, ApplicationRequestModel} from '../../../../../src/models/api/applicationModel';
import {CustomFieldModel} from '../../../../../src/models/api/customFieldModel';
import {TagModel} from '../../../../../src/models/api/tagModel';
import {TopicModel} from '../../../../../src/models/api/topicModel';

export enum VisualBasicActivityTypesEnum {
  ASSIGNED_USER_THEMSELVES = 'ASSIGNED_USER_THEMSELVES',
  ASSIGNED_TEAMMATE_THEMSELVES = 'ASSIGNED_TEAMMATE_THEMSELVES',
  ASSIGNED_BY_TEAMMATE = 'ASSIGNED_BY_TEAMMATE',
  ASSIGNED_BY_RULE = 'ASSIGNED_BY_RULE',
  ASSIGNED_BY_API = 'ASSIGNED_BY_API',
  ASSIGNED_OTHER = 'ASSIGNED_OTHER',

  MOVED_BY_TEAMMATE = 'MOVED_BY_TEAMMATE',
  MOVED_BY_RULE = 'MOVED_BY_RULE',
  MOVED_BY_OTHER = 'MOVED_BY_OTHER',

  TAG = 'TAG',
  UNTAG = 'UNTAG',

  TOPIC_CLOSED = 'TOPIC_CLOSED',
  TOPIC_ADDED = 'TOPIC_ADDED',
  TOPIC_REMOVED = 'TOPIC_REMOVED',

  CUSTOM_ATTRIBUTE_UPDATED = 'CUSTOM_ATTRIBUTE_UPDATED',

  ERROR = 'ERROR',

  FORWARDED_BY_RULE = 'FORWARDED_BY_RULE',
  FORWARDED_BY_OTHER = 'FORWARDED_BY_OTHER',

  TRACKER_ARCHIVE_USER_BY_RULE = 'TRACKER_ARCHIVE_USER_BY_RULE',
  TRACKER_SHARE_USER_BY_TEAMMATE = 'TRACKER_SHARE_USER_BY_TEAMMATE',
  TRACKER_SHARE_BY_TEAMMATE = 'TRACKER_SHARE_BY_TEAMMATE',
  TRACKER_SHARE_BY_RULE = 'TRACKER_SHARE_BY_RULE',
  TRACKER_SHARE_BY_DELETED_RULE = 'TRACKER_SHARE_BY_DELETED_RULE',
  TRACKER_SHARE_OTHER = 'TRACKER_SHARE_OTHER',

  TRACKER_UNSHARE_BY_TEAMMATE = 'TRACKER_UNSHARE_BY_TEAMMATE',
  TRACKER_UNSHARE_USER_BY_TEAMMATE = 'TRACKER_UNSHARE_USER_BY_TEAMMATE',

  UNASSIGNED_INACTIVITY = 'UNASSIGNED_INACTIVITY',
  UNASSIGNED_TEAMMATE = 'UNASSIGNED_TEAMMATE',
  UNASSIGNED_USER = 'UNASSIGNED_USER',
  UNASSIGNED_OOO = 'UNASSIGNED_OOO',
  UNASSIGNED_DELETED_TEAMMATE = 'UNASSIGNED_DELETED_TEAMMATE',
  UNASSIGNED_BY_RULE = 'UNASSIGNED_BY_RULE',
  UNASSIGNED_OTHER = 'UNASSIGNED_OTHER',

  ARCHIVED_SNOOZE_TEAMMATE = 'ARCHIVED_SNOOZE_TEAMMATE',
  ARCHIVED_SNOOZE_RULE = 'ARCHIVED_SNOOZE_RULE',
  ARCHIVED_SNOOZE_OTHER = 'ARCHIVED_SNOOZE_OTHER',
  ARCHIVED_TEAMMATE = 'ARCHIVED_TEAMMATE',
  ARCHIVED_RULE = 'ARCHIVED_RULE',
  ARCHIVED_DECISION_TREE = 'ARCHIVED_DECISION_TREE',
  ARCHIVED_OTHER = 'ARCHIVED_OTHER',

  REOPEN_USER = 'REOPEN_USER',
  REOPEN_TEAMMATE = 'REOPEN_TEAMMATE',
  REOPEN_RULE = 'REOPEN_RULE',
  REOPEN_MESSAGE_SENT = 'REOPEN_MESSAGE_SENT',
  REOPEN_OTHER = 'REOPEN_OTHER',

  TRASHED_TEAMMATE = 'TRASHED_TEAMMATE',
  TRASHED_SPAM = 'TRASHED_SPAM',
  TRASHED_DATA_RETENTION = 'TRASHED_DATA_RETENTION',
  TRASHED_OTHER = 'TRASHED_OTHER',

  MERGE_TEAMMATE = 'MERGE_TEAMMATE',
  MERGE_OTHER = 'MERGE_OTHER',

  RESTORE_TEAMMATE = 'RESTORE_TEAMMATE',
  RESTORE_OTHER = 'RESTORE_OTHER',

  RULE_NOTIFIED_EXISTING_RULE = 'RULE_NOTIFIED_EXISTING_RULE',
  RULE_NOTIFIED_DELETED_RULE = 'RULE_NOTIFIED_DELETED_RULE',
  RULE_NOTIFIED_OTHER = 'RULE_NOTIFIED_OTHER',

  REMINDER_EXPIRED = 'REMINDER_EXPIRED',

  SPLIT_ORIGIN_TEAMMATE = 'SPLIT_ORIGIN_TEAMMATE',
  SPLIT_ORIGIN_RULE = 'SPLIT_ORIGIN_RULE',
  SPLIT_ORIGIN_DATA_LEAK = 'SPLIT_ORIGIN_DATA_LEAK',
  SPLIT_ORIGIN_OTHER = 'SPLIT_ORIGIN_OTHER',

  SPLIT_TARGET_RULE = 'SPLIT_TARGET_RULE',
  SPLIT_TARGET_OTHER = 'SPLIT_TARGET_OTHER',

  AUTO_SPLIT_ORIGIN_INACTIVITY = 'AUTO_SPLIT_ORIGIN_INACTIVITY',
  AUTO_SPLIT_ORIGIN_LINK_CONVERSATION = 'AUTO_SPLIT_ORIGIN_LINK_CONVERSATION',

  CONVERSATION_STAGE_UPDATE_BY_COMPANY = 'CONVERSATION_STAGE_UPDATE_BY_COMPANY',
  CONVERSATION_STAGE_UPDATE_BY_RULE = 'CONVERSATION_STAGE_UPDATE_BY_RULE',

  FRONT_CHAT_ENDED = 'FRONT_CHAT_ENDED',
  FRONT_CHAT_TRANSCRIPT_SENT = 'FRONT_CHAT_TRANSCRIPT_SENT',
  FRONT_CHAT_TRANSCRIPT_EMAIL = 'FRONT_CHAT_TRANSCRIPT_EMAIL',

  CONVERSATION_UNLINKED = 'CONVERSATION_UNLINKED',

  ACTION_SKIPPED = 'ACTION_SKIPPED',

  EXECUTE_APPLICATION_REQUEST = 'EXECUTE_APPLICATION_REQUEST',

  TICKET_STATUS_UPDATE = 'TICKET_STATUS_UPDATE',
}

interface AssignedThemselvesUserVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ASSIGNED_USER_THEMSELVES;
}

interface AssignedThemselvesTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ASSIGNED_TEAMMATE_THEMSELVES;
  assignee: TeammateModel;
}

interface AssignedTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ASSIGNED_BY_TEAMMATE;
  assignee: TeammateModel;
  sourceTeammate: TeammateModel;
}

interface AssignedRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ASSIGNED_BY_RULE;
  assignee: TeammateModel;

  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

interface AssignedApiVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ASSIGNED_BY_API;
  assignee: TeammateModel;
  source: ActivitySourceOtherModel;
}

interface AssignedOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ASSIGNED_OTHER;
  assignee: TeammateModel;
}

interface MovedByTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.MOVED_BY_TEAMMATE;
  inbox: InboxModel;
  sourceTeammate: TeammateModel;
}

interface MovedByRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.MOVED_BY_RULE;
  inbox: InboxModel;
  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

interface MovedOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.MOVED_BY_OTHER;
  inbox: InboxModel;
}

export interface TaggedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TAG;
  tag: TagModel;

  macroRequester?: TeammateModel;
  sourceTeammate: TeammateModel | undefined;
  decisionTreeId: number | undefined;
  decisionTreeName: string | undefined;
  decisionTreeNamespace: string | undefined;
}

export interface UnTaggedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNTAG;
  tag: TagModel;

  macroRequester?: TeammateModel;
  sourceTeammate: TeammateModel | undefined;
}

export interface TopicClosedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TOPIC_CLOSED;
  topic: TopicModel;

  macroRequester?: TeammateModel;
  sourceTeammate: TeammateModel | undefined;
}

export interface TopicAddedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TOPIC_ADDED;
  topic: TopicModel;

  macroRequester?: TeammateModel;
  sourceTeammate: TeammateModel | undefined;
}

export interface TopicRemovedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TOPIC_REMOVED;
  topic: TopicModel;

  macroRequester?: TeammateModel;
  sourceTeammate: TeammateModel | undefined;
}

export interface CustomAttributeUpdatedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.CUSTOM_ATTRIBUTE_UPDATED;
  customField: CustomFieldModel;

  macroRequester?: TeammateModel;
  sourceTeammate: TeammateModel | undefined;
}

interface ErrorVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ERROR;
  activityError: string | undefined;
  activitySource: ActivitySourceContactModel;
}

interface ForwardedByRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.FORWARDED_BY_RULE;
  contactHandle: string;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;

  macroRequester?: TeammateModel;
}

interface ForwardedOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.FORWARDED_BY_OTHER;
  contactHandle: string;
}

interface TrackerArchiveUserByRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_ARCHIVE_USER_BY_RULE;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;

  macroRequester?: TeammateModel;
}

interface TrackerShareUserByTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_SHARE_USER_BY_TEAMMATE;
  sourceTeammate: TeammateModel;
  recipient: TeammateModel;
}

interface TrackerShareTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_TEAMMATE;
  sourceTeammate: TeammateModel;
  recipient: TeammateModel;
}

interface TrackerShareRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_RULE;
  recipient: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
  macroRequester?: TeammateModel;
}

interface TrackerShareRuleDeletedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_SHARE_BY_DELETED_RULE;
  recipient: TeammateModel;
}

interface TrackerShareOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_SHARE_OTHER;
  recipient: TeammateModel;
}

interface TrackerUnshareTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_UNSHARE_BY_TEAMMATE;
  sourceTeammate: TeammateModel;
  recipient: TeammateModel;
}

interface TrackerUnshareUserByTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRACKER_UNSHARE_USER_BY_TEAMMATE;
  sourceTeammate: TeammateModel;
  recipient: TeammateModel;
}

interface UnassignedInactivityVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNASSIGNED_INACTIVITY;
}

interface UnassignedUserVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNASSIGNED_USER;
}

interface UnassignedTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNASSIGNED_TEAMMATE;
  sourceTeammate: TeammateModel;
}

interface UnassignedOutOfOfficeVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNASSIGNED_OOO;
  sourceTeammate: TeammateModel;
}

interface UnassignedDeletedTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNASSIGNED_DELETED_TEAMMATE;
}

interface UnassignedByRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNASSIGNED_BY_RULE;
  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

interface UnassignedOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.UNASSIGNED_OTHER;
}

export interface TicketStatusUpdateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TICKET_STATUS_UPDATE;
  tag: TagModel;
  sourceTeammate: TeammateModel | undefined;
  ticketStatus: TicketStatusModel | undefined;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
}

interface ArchivedSnoozeTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_TEAMMATE;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
  sourceTeammate: TeammateModel;
}

interface ArchivedSnoozeRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_RULE;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

interface ArchivedSnoozeOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ARCHIVED_SNOOZE_OTHER;
}

interface ArchivedTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ARCHIVED_TEAMMATE;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
  sourceTeammate: TeammateModel;
}

interface ArchivedRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ARCHIVED_RULE;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

export interface ArchivedDecisionTreeVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ARCHIVED_DECISION_TREE;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
  decisionTreeId: number;
  decisionTreeName: string;
  decisionTreeNamespace: string;
}

interface ArchivedOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.ARCHIVED_OTHER;
}

interface ReopenUserVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.REOPEN_USER;
}

interface ReopenTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.REOPEN_TEAMMATE;
  sourceTeammate: TeammateModel;
}

interface ReopenRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.REOPEN_RULE;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
  macroRequester?: TeammateModel;
}

interface ReopenMessageSentVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.REOPEN_MESSAGE_SENT;
}

interface ReopenOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.REOPEN_OTHER;
}

interface TrashedTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRASHED_TEAMMATE;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
  sourceTeammate: TeammateModel;
}

interface TrashedSpamVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRASHED_SPAM;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
}

interface TrashedDataRetentionVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRASHED_DATA_RETENTION;
}

interface TrashedOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.TRASHED_OTHER;
}

interface MergeTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.MERGE_TEAMMATE;
  sourceTeammate: TeammateModel;
}

interface MergeOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.MERGE_OTHER;
}

interface RestoreTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.RESTORE_TEAMMATE;
  originalTeamInboxes: ReadonlyArray<InboxModel>;
  sourceTeammate: TeammateModel;
}

interface RestoreOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.RESTORE_OTHER;
}

interface RuleNotifiedExistingVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.RULE_NOTIFIED_EXISTING_RULE;
  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

interface RuleNotifiedDeletedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.RULE_NOTIFIED_DELETED_RULE;
}

interface RuleNotifiedOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.RULE_NOTIFIED_OTHER;
}

interface ReminderVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.REMINDER_EXPIRED;
}

interface SplitOriginTeammateVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_TEAMMATE;
  sourceTeammate: TeammateModel;
  targetConversationId: number;
}

interface SplitOriginRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_RULE;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;

  targetConversationId: number;
  macroRequester?: TeammateModel;
}

interface SplitOriginDataLeakVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_DATA_LEAK;
  targetConversationId: number;
}

interface SplitOriginOtherVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.SPLIT_ORIGIN_OTHER;
}

interface SplitTargetVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.SPLIT_TARGET_OTHER;
}

interface SplitTargetRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.SPLIT_TARGET_RULE;
  originConversationId: number;

  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
  macroRequester?: TeammateModel;
}

interface AutoSplitOriginInactivityVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.AUTO_SPLIT_ORIGIN_INACTIVITY;
  targetConversationId: number;
}

interface AutoSplitOriginLinkConversationVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.AUTO_SPLIT_ORIGIN_LINK_CONVERSATION;
  targetConversationId: number;
}

interface FrontChatTranscriptSentVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_SENT;
  transcriptRecipientHandle: string;
}

interface FrontChatEndedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.FRONT_CHAT_ENDED;
  decisionTreeId: number | undefined;
  decisionTreeName: string | undefined;
  decisionTreeNamespace: string | undefined;
}

interface FrontChatTranscriptEmailVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.FRONT_CHAT_TRANSCRIPT_EMAIL;
  transcriptRecipientHandle: string;
}

export interface ConversationStageUpdateByCompanyVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.CONVERSATION_STAGE_UPDATE_BY_COMPANY;
  conversationStageType: ConversationStageType;
  source: ActivitySourceModel;
}

export interface ConversationStageUpdateByRuleVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.CONVERSATION_STAGE_UPDATE_BY_RULE;
  conversationStageType: ConversationStageType;

  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

interface ConversationUnlinkedVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.CONVERSATION_UNLINKED;
  sourceTeammate: TeammateModel;
}

export interface ActionSkippedBasicActivity {
  type: VisualBasicActivityTypesEnum.ACTION_SKIPPED;
  skippedActionType: SkippedActionTypesEnum;
  skipReason: SkippedActionReasonsEnum;
  source: ActivitySourceModel;
}

export interface ExecuteApplicationRequestVisualBasicActivity {
  type: VisualBasicActivityTypesEnum.EXECUTE_APPLICATION_REQUEST;
  application: ApplicationModel;
  applicationRequest: ApplicationRequestModel;

  macroRequester?: TeammateModel;
  source: ActivitySourceRuleModel | ActivitySourceMacroModel;
}

export type VisualBasicActivity =
  | AssignedThemselvesUserVisualBasicActivity
  | AssignedThemselvesTeammateVisualBasicActivity
  | AssignedTeammateVisualBasicActivity
  | AssignedOtherVisualBasicActivity
  | AssignedRuleVisualBasicActivity
  | AssignedApiVisualBasicActivity
  | MovedByTeammateVisualBasicActivity
  | MovedByRuleVisualBasicActivity
  | MovedOtherVisualBasicActivity
  | TaggedVisualBasicActivity
  | UnTaggedVisualBasicActivity
  | TopicClosedVisualBasicActivity
  | TopicAddedVisualBasicActivity
  | TopicRemovedVisualBasicActivity
  | CustomAttributeUpdatedVisualBasicActivity
  | ErrorVisualBasicActivity
  | ForwardedByRuleVisualBasicActivity
  | ForwardedOtherVisualBasicActivity
  | TrackerArchiveUserByRuleVisualBasicActivity
  | TrackerShareUserByTeammateVisualBasicActivity
  | TrackerShareTeammateVisualBasicActivity
  | TrackerShareRuleVisualBasicActivity
  | TrackerShareRuleDeletedVisualBasicActivity
  | TrackerShareOtherVisualBasicActivity
  | TrackerUnshareTeammateVisualBasicActivity
  | TrackerUnshareUserByTeammateVisualBasicActivity
  | UnassignedInactivityVisualBasicActivity
  | UnassignedUserVisualBasicActivity
  | UnassignedTeammateVisualBasicActivity
  | UnassignedOutOfOfficeVisualBasicActivity
  | UnassignedDeletedTeammateVisualBasicActivity
  | UnassignedByRuleVisualBasicActivity
  | UnassignedOtherVisualBasicActivity
  | ArchivedSnoozeTeammateVisualBasicActivity
  | ArchivedSnoozeRuleVisualBasicActivity
  | ArchivedSnoozeOtherVisualBasicActivity
  | ArchivedTeammateVisualBasicActivity
  | ArchivedRuleVisualBasicActivity
  | ArchivedDecisionTreeVisualBasicActivity
  | ArchivedOtherVisualBasicActivity
  | ReopenUserVisualBasicActivity
  | ReopenTeammateVisualBasicActivity
  | ReopenRuleVisualBasicActivity
  | ReopenMessageSentVisualBasicActivity
  | ReopenOtherVisualBasicActivity
  | TrashedTeammateVisualBasicActivity
  | TrashedSpamVisualBasicActivity
  | TrashedDataRetentionVisualBasicActivity
  | TrashedOtherVisualBasicActivity
  | MergeTeammateVisualBasicActivity
  | MergeOtherVisualBasicActivity
  | RestoreTeammateVisualBasicActivity
  | RestoreOtherVisualBasicActivity
  | RuleNotifiedExistingVisualBasicActivity
  | RuleNotifiedDeletedVisualBasicActivity
  | RuleNotifiedOtherVisualBasicActivity
  | ReminderVisualBasicActivity
  | SplitOriginTeammateVisualBasicActivity
  | SplitOriginRuleVisualBasicActivity
  | SplitOriginDataLeakVisualBasicActivity
  | SplitOriginOtherVisualBasicActivity
  | SplitTargetVisualBasicActivity
  | SplitTargetRuleVisualBasicActivity
  | AutoSplitOriginInactivityVisualBasicActivity
  | AutoSplitOriginLinkConversationVisualBasicActivity
  | FrontChatEndedVisualBasicActivity
  | FrontChatTranscriptEmailVisualBasicActivity
  | FrontChatTranscriptSentVisualBasicActivity
  | ConversationStageUpdateByCompanyVisualBasicActivity
  | ConversationStageUpdateByRuleVisualBasicActivity
  | ConversationUnlinkedVisualBasicActivity
  | ActionSkippedBasicActivity
  | ExecuteApplicationRequestVisualBasicActivity
  | TicketStatusUpdateVisualBasicActivity;
